import { useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { clearFilters, fetchSavedViews } from "redux/SingleDataset/actions";
import { useNavigate, useParams, useLocation } from "react-router";
import { SavedView } from "types";
import { State } from "redux/store";
import { getSavedViews } from "redux/SingleDataset/selectors";
import styles from "./style.module.scss";
import classNames from "classnames";
import { FaRegStar, FaStar } from "react-icons/fa";
import { GoArrowRight } from "react-icons/go";
import useNavigateToView from "hooks/useNavigateToView";
import { AMP_SAVED_VIEWS_EVENT__VIEW_EXPLORED } from "helpers/constants/amplitudeEvents";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { NoViewsComponent } from "views/pages/SavedViewsPage/NoViewComponent";
import {
  PAGE_PARAMETER,
  SAVED_VIEW_PARAMETER,
} from "helpers/constants/miscellaneous";
import FilterButton from "views/atomComponents/FilterButton";
import useQueryParam from "hooks/useQueryParam";
interface SavedViewsFilterProps {
  savedViews: SavedView[];
}

const SavedViewsFilter = ({ savedViews }: SavedViewsFilterProps) => {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const navigateToView = useNavigateToView();
  const location = useLocation();
  const prevSearchRef = useRef(location.search);
  const selectedViewId = useQueryParam(SAVED_VIEW_PARAMETER);
  const selectedSavedView = savedViews.find(
    (view) => view.id === selectedViewId
  );

  useEffect(() => {
    dispatch(fetchSavedViews(datasetId) as any);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(location.search);
    const prevSearchParams = new URLSearchParams(prevSearchRef.current);

    const currentSavedView = currentSearchParams.get(SAVED_VIEW_PARAMETER);
    const prevSavedView = prevSearchParams.get(SAVED_VIEW_PARAMETER);

    const ignoredFilters = [PAGE_PARAMETER];

    ignoredFilters.forEach((filter) => {
      currentSearchParams.delete(filter);
      prevSearchParams.delete(filter);
    });

    const currentSearchString = currentSearchParams.toString();
    const prevSearchString = prevSearchParams.toString();

    // If the saved view is still the same and other filters changed, remove the saved view
    if (
      currentSavedView &&
      currentSavedView === prevSavedView &&
      currentSearchString !== prevSearchString
    ) {
      let newSearchParam = new URLSearchParams(location.search);
      newSearchParam.delete(SAVED_VIEW_PARAMETER);
      navigate({ search: newSearchParam.toString() }, { replace: true });
    }

    prevSearchRef.current = location.search;

    //eslint-disable-next-line
  }, [location.search]);

  const handleSeeAllViewsClick = () => {
    navigate(`/dataset/${datasetId}/views`);
  };
  const handleSavedViewClick = async (savedView: SavedView) => {
    if (selectedSavedView?.id === savedView.id) {
      dispatch(clearFilters() as any);
      // Preserve page parameter when clearing saved view
      const newSearchParams = new URLSearchParams();
      newSearchParams.set(PAGE_PARAMETER, "1");
      navigate({ search: newSearchParams.toString() });
    } else {
      amplitudeTrack(AMP_SAVED_VIEWS_EVENT__VIEW_EXPLORED);
      navigateToView(savedView);
    }
  };
  const title = selectedSavedView ? selectedSavedView.name : "Views";

  const reorderedSavedViews = selectedSavedView
    ? [
        selectedSavedView,
        ...savedViews.filter((view) => view.id !== selectedSavedView.id),
      ]
    : savedViews;

  return (
    <FilterButton
      label={title}
      iconLeft={selectedSavedView ? <FaStar /> : <FaRegStar />}
      variant={!!selectedSavedView ? "selected" : "default"}
      tooltip={`Apply this filter to display only the data entries from a specific saved view.`}
      closeOnSelection={true}
    >
      {savedViews.length === 0 && (
        <div className={styles.noViewsContainer}>
          <NoViewsComponent />
        </div>
      )}
      {savedViews.length > 0 && (
        <div className={styles.dropdownContainer}>
          <div className={styles.dropdownItems}>
            {reorderedSavedViews.map((savedView) => (
              <div
                className={classNames(styles.dropdownItem, {
                  [styles.selectedDropdownItem]:
                    selectedSavedView?.id === savedView.id,
                })}
                key={savedView.id}
                onClick={() => handleSavedViewClick(savedView)}
                data-selected={selectedSavedView?.id === savedView.id}
              >
                {savedView.name}
              </div>
            ))}
          </div>
          <div
            className={styles.dropdownFooter}
            onClick={handleSeeAllViewsClick}
            data-testid="see-all-views-button"
          >
            <div className={styles.dropdownFooterText}>
              {"See all views"} <GoArrowRight />
            </div>
          </div>
        </div>
      )}
    </FilterButton>
  );
};

const mapStateToProps = (state: State) => ({
  savedViews: getSavedViews(state),
});

export default connect(mapStateToProps)(SavedViewsFilter);
